import { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { identifyAnalyticsUser, trackEvent } from './services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from './services/analytics/analyticsEvents';
import { INCOMPLETE_PROFILE_REMINDER_SESSION_KEY } from './utils/onboarding';
import { getPostLoginDestination, isRegistrationComplete } from './utils/userCompletion';

const MagicLogin = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setUser } = useContext(UserContext) || {};

    useEffect(() => {
        const run = async () => {
            const redirectToLoginWithError = (msg) => {
                navigate(`/?magicLinkError=${encodeURIComponent(msg)}`, { replace: true });
            };

            try {
                const params = new URLSearchParams(location.search);
                const email = params.get('email') || '';
                const token = params.get('token') || '';

                if (!email || !token) {
                    redirectToLoginWithError('Invalid or expired link.');
                    return;
                }

                // ✅ verify magic link
                const res = await api.post('/api/auth/magic-link/verify', { email, token });

                const jwtToken = res.data?.token;

                if (!jwtToken) {
                    redirectToLoginWithError('Invalid or expired link.');
                    return;
                }

                // ✅ store token
                localStorage.setItem('token', jwtToken);

                // ✅ attach token for immediate requests
                api.defaults.headers.common['x-auth-token'] = jwtToken;
                api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

                // ✅ fetch FULL user (this fixes profileImage issue)
                const userResponse = await api.get('/api/users/me');
                const fullUser = userResponse.data;

                localStorage.setItem('user', JSON.stringify(fullUser));
                sessionStorage.removeItem(INCOMPLETE_PROFILE_REMINDER_SESSION_KEY);

                if (typeof setUser === 'function') setUser(fullUser);

                identifyAnalyticsUser(fullUser);

                const redirectParam = params.get('redirect') || '';
                const isRegistrationMagicLink = redirectParam.includes('/register');

                trackEvent(
                    isRegistrationMagicLink
                        ? ANALYTICS_EVENTS.REGISTRATION_MAGIC_LINK_VERIFIED
                        : ANALYTICS_EVENTS.MAGIC_LINK_VERIFIED,
                    {
                        loginMethod: 'magic_link',
                        magicLinkPurpose: isRegistrationMagicLink ? 'registration' : 'login',
                        role: fullUser?.role || '',
                        registrationCompleted: Boolean(fullUser?.registrationCompleted),
                        profileCompleted: Boolean(fullUser?.profileCompleted),
                    }
                );

                // use full user from here
                const role = fullUser?.role;

                let defaultRedirect = '/';

                if (role === 'student') {
                    defaultRedirect = getPostLoginDestination(fullUser, '/discovery/companies');
                } else if (role === 'donatorUser') {
                    defaultRedirect = '/donations-my';
                } else if (['admin', 'titanAdmin', 'superadmin'].includes(role)) {
                    defaultRedirect = '/ticket-list';
                } else if (role === 'company') {
                    defaultRedirect = '/dashboard-company';
                }

                const redirect = isRegistrationMagicLink && isRegistrationComplete(fullUser)
                    ? defaultRedirect
                    : redirectParam || defaultRedirect;
                navigate(redirect, { replace: true });
            } catch (err) {
                console.error('Magic login failed:', err);
                redirectToLoginWithError(err.response?.data?.message || 'Invalid or expired link.');
            }
        };

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div style={{ padding: 24 }}>Logging you in…</div>;
};

export default MagicLogin;
